import React, { useState } from 'react';

import { connect, useDispatch } from 'react-redux';

import { Button, Card, Col, Form, Input, Modal, Row, Select, message } from 'antd';

import { api } from '../../http/API';
import { Company, CompanyType } from '../../models/Company';
import { User } from '../../models/User';
import { saveCompany } from '../../storage/CompanyStorage';
import { saveUser } from '../../storage/UserStorage';

interface Props {
    user: User;
    company: Company;
}

const ProfilePage = (props: Props) => {
    const dispatch = useDispatch();

    const [accountType, setAccountType] = useState<CompanyType>(
        (props.company?.company_type as CompanyType) || 'individual',
    );
    const [saving, setSaving] = useState(false);

    const [securityOpen, setSecurityOpen] = useState(false);
    const [passwordOpen, setPasswordOpen] = useState(false);

    const [individualForm] = Form.useForm();
    const [legalForm] = Form.useForm();
    const [securityForm] = Form.useForm();
    const [passwordForm] = Form.useForm();

    // ===== Сохранение физлица =====
    const saveIndividual = async (values: any) => {
        setSaving(true);
        try {
            await api.EditUser(
                props.user.email,
                values.name,
                values.surname,
                values.patronymic,
                props.user.phone_number,
            );
            // Тип аккаунта возвращаем на individual
            await api.EditCompany({
                name: props.company.name,
                description: props.company.description,
                address: props.company.address,
                phone: props.company.phone,
                company_type: 'individual',
            });
            dispatch(saveCompany({ ...props.company, company_type: 'individual' }));
            dispatch(
                saveUser({
                    ...props.user,
                    name: values.name,
                    surname: values.surname,
                    patronymic: values.patronymic,
                }),
            );
            setAccountType('individual');
            message.success('Личные данные сохранены');
        } catch {
            message.error('Не удалось сохранить личные данные');
        } finally {
            setSaving(false);
        }
    };

    // ===== Сохранение юрлица =====
    const saveLegal = async (values: any) => {
        setSaving(true);
        try {
            const payload = {
                name: values.name,
                company_type: 'legal' as const,
                inn: values.inn,
                kpp: values.kpp,
                bank_name: values.bank_name,
                account: values.account,
                corr_account: values.corr_account,
                bik: values.bik,
                legal_address: values.legal_address,
                postal_address: values.postal_address,
                accounting_email: values.accounting_email,
                contact_name: values.contact_name,
                contact_email: values.contact_email,
            };
            await api.EditCompany(payload);
            dispatch(saveCompany({ ...props.company, ...payload }));
            setAccountType('legal');
            message.success('Данные организации сохранены');
        } catch {
            message.error('Не удалось сохранить данные организации');
        } finally {
            setSaving(false);
        }
    };

    // ===== Безопасность и вход =====
    const saveSecurity = async (values: any) => {
        try {
            await api.EditUser(
                values.email,
                props.user.name,
                props.user.surname,
                props.user.patronymic,
                values.phone_number,
            );
            dispatch(saveUser({ ...props.user, email: values.email, phone_number: values.phone_number }));
            setSecurityOpen(false);
            message.success('Данные для входа сохранены');
        } catch {
            message.error('Не удалось сохранить');
        }
    };

    // ===== Смена пароля =====
    const savePassword = async (values: any) => {
        if (values.new_password !== values.repeat_password) {
            message.error('Пароли не совпадают');
            return;
        }
        try {
            await api.ChangePassword(values.password, values.new_password);
            setPasswordOpen(false);
            passwordForm.resetFields();
            message.success('Пароль изменён');
        } catch {
            message.error('Не удалось изменить пароль');
        }
    };

    return (
        <div>
            {/* Верхняя панель: тип аккаунта + безопасность */}
            <div className="profile-topbar">
                <div className="profile-topbar-group">
                    <span className="profile-topbar-label">Тип аккаунта</span>
                    <Select
                        value={accountType}
                        onChange={(v) => setAccountType(v as CompanyType)}
                        style={{ minWidth: 200 }}
                        options={[
                            { value: 'individual', label: 'Физическое лицо' },
                            { value: 'legal', label: 'Юридическое лицо' },
                        ]}
                    />
                    <Button onClick={() => setSecurityOpen(true)}>Безопасность и вход</Button>
                </div>
            </div>

            {accountType === 'individual' ? (
                <Card title="Физическое лицо" style={{ maxWidth: 720 }}>
                    <Form
                        form={individualForm}
                        layout="vertical"
                        onFinish={saveIndividual}
                        initialValues={{
                            surname: props.user.surname || '',
                            name: props.user.name || '',
                            patronymic: props.user.patronymic || '',
                        }}
                    >
                        <Row gutter={16}>
                            <Col xs={24} md={8}>
                                <Form.Item label="Фамилия" name="surname">
                                    <Input placeholder="Иванов" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Имя" name="name">
                                    <Input placeholder="Иван" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Отчество" name="patronymic">
                                    <Input placeholder="Иванович" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            Сохранить
                        </Button>
                    </Form>
                </Card>
            ) : (
                <Card title="Юридическое лицо" style={{ maxWidth: 720 }}>
                    <div className="legal-note">
                        После сохранения название организации и ИНН изменить нельзя. Для этого потребуется создать нового
                        плательщика.
                    </div>
                    <Form
                        form={legalForm}
                        layout="vertical"
                        onFinish={saveLegal}
                        initialValues={{
                            name: props.company.name || '',
                            inn: props.company.inn || '',
                            kpp: props.company.kpp || '',
                            bank_name: props.company.bank_name || '',
                            account: props.company.account || '',
                            corr_account: props.company.corr_account || '',
                            bik: props.company.bik || '',
                            legal_address: props.company.legal_address || '',
                            postal_address: props.company.postal_address || '',
                            accounting_email: props.company.accounting_email || '',
                            contact_name: props.company.contact_name || '',
                            contact_email: props.company.contact_email || '',
                        }}
                    >
                        <Form.Item label="Название организации" name="name" rules={[{ required: true }]}>
                            <Input placeholder="ООО «Ромашка»" />
                        </Form.Item>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label="ИНН" name="inn">
                                    <Input placeholder="1234567890" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="КПП" name="kpp">
                                    <Input placeholder="123401001" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item label="Название банка" name="bank_name">
                            <Input placeholder="ООО «Банк»" />
                        </Form.Item>
                        <Form.Item label="Расчетный счет" name="account">
                            <Input placeholder="20 цифр" />
                        </Form.Item>
                        <Form.Item label="Корреспондентский счет" name="corr_account">
                            <Input placeholder="20 цифр" />
                        </Form.Item>
                        <Form.Item label="БИК банка" name="bik">
                            <Input placeholder="9 цифр" />
                        </Form.Item>
                        <Form.Item label="Юридический адрес" name="legal_address">
                            <Input placeholder="Индекс, город, улица, дом, квартира" />
                        </Form.Item>
                        <Form.Item label="Почтовый адрес" name="postal_address">
                            <Input placeholder="Индекс, город, улица, дом, квартира" />
                        </Form.Item>
                        <Form.Item label="Емейл бухгалтерии" name="accounting_email">
                            <Input placeholder="accounting@example.ru" />
                        </Form.Item>
                        <Form.Item label="Контактное лицо" name="contact_name">
                            <Input placeholder="Имя" />
                        </Form.Item>
                        <Form.Item label="Контактный емейл" name="contact_email">
                            <Input placeholder="contact@example.ru" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            Сохранить
                        </Button>
                    </Form>
                </Card>
            )}

            {/* Модалка: Безопасность и вход */}
            <Modal
                title="Безопасность и вход"
                open={securityOpen}
                onCancel={() => setSecurityOpen(false)}
                onOk={() => securityForm.submit()}
                okText="Сохранить"
                cancelText="Отмена"
            >
                <Form
                    form={securityForm}
                    layout="vertical"
                    onFinish={saveSecurity}
                    initialValues={{
                        email: props.user.email || '',
                        phone_number: props.user.phone_number || '',
                    }}
                >
                    <Form.Item label="Email" name="email">
                        <Input placeholder="example@mail.ru" />
                    </Form.Item>
                    <Form.Item label="Телефон" name="phone_number">
                        <Input placeholder="+7 (___) ___-__-__" />
                    </Form.Item>
                    <Button type="link" onClick={() => setPasswordOpen(true)} style={{ padding: 0 }}>
                        Сменить пароль →
                    </Button>
                </Form>
            </Modal>

            {/* Модалка: Смена пароля */}
            <Modal
                title="Смена пароля"
                open={passwordOpen}
                onCancel={() => setPasswordOpen(false)}
                onOk={() => passwordForm.submit()}
                okText="Сохранить"
                cancelText="Отмена"
            >
                <Form form={passwordForm} layout="vertical" onFinish={savePassword}>
                    <Form.Item label="Текущий пароль" name="password" rules={[{ required: true }]}>
                        <Input.Password />
                    </Form.Item>
                    <Form.Item label="Новый пароль" name="new_password" rules={[{ required: true, min: 8 }]}>
                        <Input.Password placeholder="Минимум 8 символов" />
                    </Form.Item>
                    <Form.Item label="Повторите новый пароль" name="repeat_password" rules={[{ required: true }]}>
                        <Input.Password />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    user: state.user.user,
    company: state.company.company,
});

export default connect(mapStateToProps)(ProfilePage);
